/**
 * Inbound Filters Hooks
 * Jotai atoms and hooks for inbound filter config.
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { inboundFilterService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { helpdeskTicketFieldsQuery } from './helpdesk-ticket-fields-query';
import type { UpdateInboundFilterDto } from '@/types';

// The brand is in every settings key as well as in the request. Two brands must never
// read each other's cached settings, and switching brands no longer clears the cache —
// a key without the brand would serve the previous brand's values.
const inboundFiltersQueryAtom = atomWithQuery((get) => ({
  queryKey: ['inbound-filters', buScopeKey(get)],
  queryFn: () => inboundFilterService.getInboundFilters(buScopeParams(get)),
  // refetchOnWindowFocus intentionally off -- useEffect syncs local state
  // from `data`, so a refocus-triggered refetch could race an in-flight
  // optimistic toggle and reset it. Single-admin settings page doesn't
  // need live updates.
  refetchOnWindowFocus: false,
}));

const inboundFilterTicketFieldsQueryAtom = atomWithQuery((get) =>
  helpdeskTicketFieldsQuery(get, get(inboundFiltersQueryAtom).data?.autoClose === true),
);

const updateInboundFiltersMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['inbound-filters', 'update'],
  mutationFn: (data: UpdateInboundFilterDto) => inboundFilterService.updateInboundFilters(data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['inbound-filters']);
  },
}));

export function useInboundFilters() {
  const [query] = useAtom(inboundFiltersQueryAtom);
  return query;
}

export function useInboundFilterTicketFields() {
  const [query] = useAtom(inboundFilterTicketFieldsQueryAtom);
  return query;
}

export function useUpdateInboundFilters() {
  const [mutation] = useAtom(updateInboundFiltersMutationAtom);
  return mutation;
}
